import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Wand2, RefreshCw, Lock, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import api from '../utils/api';
import usePlanLimits from '../hooks/usePlanLimits';

const BLOCKED_PLANS = ['freemium', 'free'];
const LOW_BALANCE_THRESHOLD = 5; // show the top-up nudge at/below this many generations

const QUICK_REFINES = [
  { label: 'Shorter', instruction: 'Make this shorter and more concise.' },
  { label: 'Longer', instruction: 'Expand this with more detail.' },
  { label: 'More formal', instruction: 'Rewrite this in a more formal, professional tone.' },
  { label: 'Friendlier', instruction: 'Rewrite this in a warmer, friendlier tone.' },
  { label: 'Fix grammar', instruction: 'Fix any grammar and spelling mistakes without changing the meaning.' },
];

/**
 * AiTextAssistant
 * A self-contained AI writing assistant for the TipTap toolbar.
 * - Hidden for Freemium/Free tiers (server also hard-blocks them).
 * - Generates HTML from a prompt, supports iterative follow-ups, and
 *   inserts the result into the editor via the `onInsert(html)` callback.
 */
const AiTextAssistant = ({ onInsert }) => {
  const { planLevel } = usePlanLimits();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [generated, setGenerated] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [buyingTopup, setBuyingTopup] = useState(false);

  const isBlocked = planLevel && BLOCKED_PLANS.includes(String(planLevel).toLowerCase());

  // Load the current balance + top-up packs whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [s, p] = await Promise.all([api.get('/ai/credits'), api.get('/ai/packages')]);
        if (!cancelled) {
          setStatus(s.data);
          setPackages(p.data.packages || []);
        }
      } catch (e) {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const unlimited = status?.unlimited;
  const remaining = status?.total_remaining;
  const isLow = !!status && !unlimited && typeof remaining === 'number' && remaining <= LOW_BALANCE_THRESHOLD;
  const smallestPack = packages.length
    ? [...packages].sort((a, b) => a.price - b.price)[0]
    : null;

  // Hide entirely for Freemium/Free tiers
  if (isBlocked) return null;

  const callAI = async (instruction, refine) => {
    if (!instruction.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    setLoading(true);
    try {
      const payload = { prompt: instruction, mode: refine ? 'refine' : 'generate' };
      if (refine && generated) payload.current_text = stripHtml(generated);
      const { data } = await api.post('/ai/assist-text', payload);
      setGenerated(data.text || '');
      if (data.status) setStatus(data.status);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402) toast.error(detail || 'Out of AI credits. Purchase more to continue.');
      else if (status === 403) toast.error(detail || 'AI is not available on your plan.');
      else toast.error(detail || 'AI generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const buyTopup = async () => {
    if (!smallestPack) return;
    setBuyingTopup(true);
    try {
      const { data } = await api.post(`/ai/purchase-credits?package=${smallestPack.id}`);
      window.open(data.url, '_blank');
      toast.success('Opening secure checkout…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setBuyingTopup(false);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleInsert = () => {
    if (!generated) return;
    onInsert(generated);
    toast.success('Content inserted');
    reset();
  };

  const reset = () => {
    setOpen(false);
    setPrompt('');
    setGenerated('');
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(true)}
              className="h-8 gap-1 px-2 text-violet-600 hover:bg-violet-50 hover:text-violet-700"
              data-testid="tiptap-ai-assist-btn"
            >
              <Sparkles size={16} />
              <span className="text-xs font-medium">AI</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Write with AI</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : reset())}>
        <DialogContent className="max-w-lg" data-testid="ai-assist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 size={18} className="text-violet-600" />
              AI Writing Assistant
            </DialogTitle>
            <DialogDescription>
              Describe what you want to write. Each generation uses 1 AI Generation.
              {status && (
                <span className="ml-1 font-medium text-slate-700">
                  {unlimited ? '(Unlimited)' : `(${remaining} left)`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {isLow && smallestPack && (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                data-testid="ai-assist-low-nudge"
              >
                <span className="text-xs text-amber-800">
                  {remaining === 0
                    ? "You're out of AI Generations this month."
                    : `Only ${remaining} AI Generation${remaining === 1 ? '' : 's'} left this month.`}
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={buyTopup}
                  disabled={buyingTopup}
                  className="h-7 gap-1 whitespace-nowrap bg-amber-600 px-2.5 text-xs hover:bg-amber-700"
                  data-testid="ai-assist-buy-topup"
                >
                  {buyingTopup ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Zap size={12} />
                  )}
                  Buy {smallestPack.credits} · ${smallestPack.price}
                </Button>
              </div>
            )}
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Write a friendly intro paragraph about our summer sale..."
              rows={3}
              className="resize-none"
              data-testid="ai-assist-prompt"
            />

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => callAI(prompt, false)}
                disabled={loading || !prompt.trim()}
                className="flex-1 gap-2 bg-violet-600 hover:bg-violet-700"
                data-testid="ai-assist-generate"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {generated ? 'Regenerate' : 'Generate'}
              </Button>
            </div>

            {generated && (
              <div className="space-y-3" data-testid="ai-assist-result">
                <div
                  className="prose prose-sm max-w-none rounded-lg border border-slate-200 bg-slate-50 p-3 max-h-56 overflow-auto"
                  dangerouslySetInnerHTML={{ __html: generated }}
                />

                <div>
                  <p className="mb-1.5 text-xs font-medium text-slate-500">Quick refine</p>
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_REFINES.map((q) => (
                      <button
                        key={q.label}
                        type="button"
                        onClick={() => callAI(q.instruction, true)}
                        disabled={loading}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50"
                        data-testid={`ai-refine-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <RefreshCw size={11} />
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    onClick={handleInsert}
                    disabled={loading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="ai-assist-insert"
                  >
                    Insert into editor
                  </Button>
                  <Button type="button" variant="outline" onClick={reset} disabled={loading}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AiTextAssistant;
